/**
 * The logo animation on the sign in screen.
 *
 * It must never read as a video. That means: no controls, no audio track at
 * all, no picture in picture, no context menu, it cannot be paused or scrubbed,
 * it plays exactly once and then holds its final frame forever.
 *
 * The supplied artwork is drawn on white, so rather than key it out (the panda
 * is white too, which makes keying eat its face) the element multiplies onto a
 * paper coloured plate. In the light theme that plate is the page colour, so
 * the animation floats with no edge at all. In the dark theme the plate reads
 * as a deliberate brand card rather than a video window.
 *
 * If the browser blocks autoplay, cannot decode either source, or the visitor
 * asks for reduced motion, the poster frame is shown instead. The poster IS the
 * final frame, so the fallback is the finished logo rather than a broken box.
 */
import { useEffect, useRef, useState } from 'react';

const REDUCED = '(prefers-reduced-motion: reduce)';

export function LogoAnimation({ width = 300 }: { width?: number }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [still, setStill] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(REDUCED).matches;
  });

  useEffect(() => {
    const el = videoRef.current;
    if (!el || still) return;
    el.muted = true;
    // A rejected play promise is normal on a locked down browser and must not
    // reach the console as an unhandled rejection.
    void el.play().catch(() => setStill(true));
  }, [still]);

  const plate = {
    width,
    aspectRatio: '880 / 462',
    background: 'var(--sp-brand-plate)',
    borderRadius: 'var(--sp-radius-lg)',
    overflow: 'hidden',
  } as const;

  if (still) {
    return (
      <div style={plate}>
        <img
          src="/brand/sp-logo-anim-poster.png"
          alt="Sneaky Panda Staff App"
          style={{ width: '100%', height: '100%', objectFit: 'cover', mixBlendMode: 'multiply' }}
        />
      </div>
    );
  }

  return (
    <div style={plate}>
      <video
        ref={videoRef}
        // No `controls`, and every affordance that would reveal it as a video
        // is switched off explicitly rather than left to browser defaults.
        autoPlay
        muted
        playsInline
        preload="auto"
        disablePictureInPicture
        controlsList="nodownload noplaybackrate nofullscreen noremoteplayback"
        poster="/brand/sp-logo-anim-poster.png"
        aria-label="Sneaky Panda Staff App"
        onContextMenu={(e) => e.preventDefault()}
        onError={() => setStill(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          // Display only: it cannot be clicked, paused or dragged.
          pointerEvents: 'none',
          mixBlendMode: 'multiply',
        }}
      >
        <source src="/brand/sp-logo-anim.webm" type="video/webm" />
        <source src="/brand/sp-logo-anim.mp4" type="video/mp4" />
      </video>
    </div>
  );
}
